import {
  Suspense,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import MCPFooter from './MCPFooter';
import MCP from './MCP';
import ConsentForm from '../../Flow/ConsentForm';
import { McpContextProvider } from './McpContextProvider';
import { useToken } from '../../Flow/ConsentContextProvider';
import { classifyScopes } from './domain/trelloScopes';
import { useTrelloWorkspaces } from './domain/getTrelloWorkspaces';

const mcpConsentMainStyle = xcss({
  backgroundColor: 'elevation.surface',
  boxSizing: 'border-box',
  padding: 'space.0',
  flex: '1 1 0%',
  minWidth: '0',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const mcpMainViewportStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const mcpFormFlexStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const contentGrowStyle = xcss({
  flex: '0 1 auto',
  minWidth: '0',
  display: 'flex',
  flexDirection: 'column',
});

const ContentGrowWrapper: FunctionComponent<PropsWithChildren> = ({
  children,
}) => <Box xcss={contentGrowStyle}>{children}</Box>;

/** Inner layout: the consent form, its content, and the fixed footer. */
const McpConsentInner: FunctionComponent = () => {
  return (
    <Box as="main" xcss={[mcpConsentMainStyle, mcpMainViewportStyle]}>
      <Box xcss={mcpFormFlexStyle}>
        <ConsentForm>
          <ContentGrowWrapper>
            <MCP />
          </ContentGrowWrapper>
          <MCPFooter />
        </ConsentForm>
      </Box>
    </Box>
  );
};

/**
 * Wrapper that fetches Trello workspaces via AGG when Trello scopes are
 * present, then passes them to McpContextProvider.  Wrapped in Suspense
 * so the Relay query can suspend without blocking the rest of the page.
 */
const TrelloWorkspaceFetcher: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { workspaces, isLoadingMore, hasMore, loadMore } =
    useTrelloWorkspaces();
  return (
    <McpContextProvider
      trelloWorkspaces={workspaces}
      trelloWorkspacesHasMore={hasMore}
      trelloWorkspacesLoadingMore={isLoadingMore}
      onLoadMoreTrelloWorkspaces={loadMore}
    >
      {children}
    </McpContextProvider>
  );
};

const McpConsentLayout: FunctionComponent = () => {
  const token = useToken();
  const { hasTrello } = classifyScopes(token.scopes ?? []);

  if (hasTrello) {
    return (
      <Suspense
        fallback={
          <McpContextProvider trelloWorkspacesLoading>
            <McpConsentInner />
          </McpContextProvider>
        }
      >
        <TrelloWorkspaceFetcher>
          <McpConsentInner />
        </TrelloWorkspaceFetcher>
      </Suspense>
    );
  }

  return (
    <McpContextProvider>
      <McpConsentInner />
    </McpContextProvider>
  );
};

export default McpConsentLayout;
